import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const AdminContext = createContext();

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
};

// Axios instance — reads token fresh on every request
export const adminApi = axios.create({ baseURL: '/api' });

// Request interceptor: attach token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401/403 clear token and redirect to login
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUsername');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export const AdminProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(() => localStorage.getItem('adminUsername') || null);
  const [token, setToken] = useState(() => localStorage.getItem('adminToken') || null);

  const login = useCallback((tok, username) => {
    localStorage.setItem('adminToken', tok);
    localStorage.setItem('adminUsername', username);
    setToken(tok);
    setAdminUser(username);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    setToken(null);
    setAdminUser(null);
  }, []);

  const isAuthenticated = !!token;

  return (
    <AdminContext.Provider value={{ adminUser, token, login, logout, isAuthenticated }}>
      {children}
    </AdminContext.Provider>
  );
};
